import { SdkContext } from '../internal'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'
import type { QuotaMeter } from './sms'

/**
 * F-142 phase 2: the community's WhatsApp channel (owner/admin): on or off
 * with the mandatory daily cap, use against the plan's quota, the approved
 * templates and the test to self. The member side (notices, consent) rides on
 * the SMS routes (`channels.whatsapp`, `smsConsent.whatsapp`). The community
 * is always the `X-CommunityId`.
 */

export interface WhatsappSettings {
  /** The period's quota (month, or the annual term where it accumulates); newer than `monthlyQuota`. */
  quota?: QuotaMeter
  enabled: boolean
  dailyCap: number | null
  dailyCapMax: number
  /** The plan's monthly quota (`whatsapp.messagesPerMonth`); `null` = no limit. */
  monthlyQuota: number | null
  /** False when the plan's `whatsapp.messagesPerMonth` is 0: the channel cannot be turned on. */
  availableOnPlan?: boolean
  /** True when the plan has no WhatsApp (`whatsapp.enabled`: Business and Scale, or a SuperAdmin override). */
  upgradeRequired?: boolean
  /** The platform's own WhatsApp messages, each with its default for people and the community's switch. */
  moments?: WhatsappMoment[]
  /** Workflow messages per person per day on this channel (set in the SMS settings). */
  personDailyCap?: number
  usage: { today: { messages: number }; month: { messages: number } }
  /** `*` = every country (global sending) except `blockedCountries`. */
  countries: string[]
  blockedCountries?: string[]
  quietHours: { start: number; end: number }
  /** Free text only up to this many hours after the person's last message. */
  windowHours: number
  timeZone: string
  purposes: string[]
}

export interface WhatsappMoment {
  key: string
  category: string
  defaultOn: boolean
  purpose: 'service' | 'marketing'
  transactional: boolean
  template: string
  /** False when the community turned it off for everyone. */
  enabled: boolean
}

export type WhatsappTemplateStatus = 'approved' | 'received' | 'pending' | 'rejected' | 'paused' | 'disabled' | 'unknown'

export interface WhatsappTemplate {
  key: string
  name: string
  category: 'UTILITY' | 'AUTHENTICATION' | string
  language: string
  variables: string[]
  status: WhatsappTemplateStatus | string
}

export interface WhatsappSendResult {
  /** `sent` right away; `skipped` with `reason`; `failed` with the provider's `code`. `queued` is an older API. */
  status: 'sent' | 'skipped' | 'failed' | 'queued'
  code?: string | null
  reason?: string | null
  messageId?: string
  notBefore?: string
  deferred?: boolean
}

/** SuperAdmin only: one community's WhatsApp on top of its plan. Cost is internal and never reaches a community. */
export interface WhatsappCommunityAccess {
  mode: 'plan' | 'on' | 'off'
  /** Whether the community has the channel now (plan + exception). */
  effective: boolean
  plan: { planNames?: string[] } | null
  /** Whether the community's team turned the channel on. */
  channelEnabled: boolean
  monthlyCostCapUsd: number | null
  spend?: {
    month: string
    whatsapp: { actualUsd: number; estimatedUsd: number; totalUsd: number }
    sms: { actualUsd: number; estimatedUsd: number; totalUsd: number }
  }
  reason: string | null
}

/** SuperAdmin only, confidential: the platform's monthly WhatsApp budget (UTC month). */
export interface WhatsappBudget {
  month: string
  budgetUsd: number | null
  budgetSource: 'superAdmin' | 'env' | 'none'
  spentUsd: number
  percent: number | null
  /** Spent in full: only payment and ticket messages keep going. */
  reached: boolean
}

export interface MessagingChannelCost { messages: number; actualUsd: number; estimatedUsd: number; totalUsd: number }

/** SuperAdmin only, confidential: what SMS and WhatsApp cost per community, channel and country in one month. */
export interface MessagingCostReport {
  month: string
  totals: Record<string, { messages: number; totalUsd: number }>
  budget?: WhatsappBudget
  top: Array<{ communityId: string; communityName: string; totalUsd: number }>
  countries?: Array<{ channel: 'sms' | 'whatsapp'; country: string | null; messages: number; actualUsd: number }>
  skippedTooExpensive?: Array<{ channel: string; country: string | null; skipped: number }>
  communities: Array<{ communityId: string; communityName: string; whatsappMonthlyCostCapUsd: number | null; channels: Record<string, MessagingChannelCost>; totalUsd: number }>
}

export class WhatsappResource {
  constructor(private ctx: SdkContext) {}

  async getSettings(): Promise<WhatsappSettings> {
    return noteRowFrom('messagingSettings', await this.ctx.call<WhatsappSettings>({ httpMethod: 'GET', path: '/whatsapp/settings' }))
  }

  /** Turning it on needs `dailyCap` (now or already saved): 422 on `dailyCap` otherwise. */
  async updateSettings(body: { enabled?: boolean; dailyCap?: number | null; moments?: Record<string, boolean> }): Promise<WhatsappSettings> {
    /* One row with SMS: one version for both cards. */
    return versionedRowWrite('messagingSettings', (v) => this.ctx.call<WhatsappSettings>({ httpMethod: 'PUT', path: '/whatsapp/settings', hasBody: true }, { body: { ...body, ...(v ? { updatedAt: v } : {}) } }))
  }

  async templates(): Promise<WhatsappTemplate[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/whatsapp/templates' })
  }

  /** A service WhatsApp to the caller's own confirmed phone, under every rule. */
  async sendTest(): Promise<WhatsappSendResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/whatsapp/test', hasBody: true }, { body: {} })
  }

  /** SuperAdmin: the community's mode (plan / on / off), its monthly cost ceiling and this month's spend. */
  async getCommunityAccess(communityId: string): Promise<WhatsappCommunityAccess> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/whatsapp' }, { communityId })
  }

  /** SuperAdmin: sets the mode and/or the cost ceiling; audited. */
  async setCommunityAccess(communityId: string, body: { mode?: 'plan' | 'on' | 'off'; monthlyCostCapUsd?: number | null; reason?: string }): Promise<WhatsappCommunityAccess> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/communities/{communityId}/whatsapp', hasBody: true }, { communityId, body })
  }

  /** SuperAdmin: the SMS and WhatsApp cost report of a UTC month (`AAAA-MM`). Never shown to a community. */
  async costs(params: { month?: string; communityId?: string } = {}): Promise<MessagingCostReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/whatsapp/costs' }, { query: params })
  }

  async getBudget(): Promise<WhatsappBudget> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/whatsapp/budget' })
  }

  /** `null` falls back to the environment's value. */
  async setBudget(monthlyBudgetUsd: number | null): Promise<WhatsappBudget> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/whatsapp/budget', hasBody: true }, { body: { monthlyBudgetUsd } })
  }
}
